import { motion } from "framer-motion";
import { ArrowDown, UserCheck } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const traditional = [
  "Problem happens",
  "Customer notices",
  "Customer contacts support",
  "Team investigates",
  "Team resolves",
];

const truvon = [
  "Problem happens",
  "Truvon detects it",
  "Investigates what happened",
  "Takes the right action",
  "Keeps the customer informed",
  "Escalates only when a human is actually needed",
];

const Flow = ({ title, badge, steps, mint, testid }) => (
  <motion.div
    initial={{ opacity: 0, y: 32 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.8, ease: EASE }}
    data-testid={testid}
    className={`rounded-2xl border p-6 sm:p-8 ${
      mint
        ? "border-mint/30 bg-gradient-to-b from-mint-dim/60 to-panel/80 shadow-[0_0_80px_rgba(31,216,166,0.08)]"
        : "border-edge bg-panel/60"
    }`}
  >
    <div className="mb-7 flex flex-wrap items-center justify-between gap-3">
      <h3 className="font-display text-lg font-bold tracking-tight text-slate-50">
        {title}
      </h3>
      <span
        className={`rounded-full px-3 py-1 font-mono text-[10px] uppercase tracking-[0.16em] ${
          mint
            ? "bg-mint-dim text-mint"
            : "bg-rose-500/10 text-rose-400"
        }`}
      >
        {badge}
      </span>
    </div>
    <ol>
      {steps.map((s, i) => (
        <motion.li
          key={s}
          initial={{ opacity: 0, x: -18 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.5, ease: EASE, delay: i * 0.1 }}
          className="relative"
        >
          <div className="flex items-center gap-3.5">
            <span
              className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border font-mono text-xs ${
                mint
                  ? "border-mint/40 bg-mint-dim text-mint"
                  : "border-edge bg-card2 text-slate-500"
              }`}
            >
              {mint && i === steps.length - 1 ? (
                <UserCheck className="h-4 w-4" />
              ) : (
                String(i + 1).padStart(2, "0")
              )}
            </span>
            <span
              className={`text-sm sm:text-base ${
                mint ? "text-slate-200" : "text-slate-400"
              } ${mint && i === steps.length - 1 ? "font-semibold text-mint" : ""}`}
            >
              {s}
            </span>
          </div>
          {i < steps.length - 1 && (
            <ArrowDown
              className={`ml-[11px] h-4 w-4 py-0.5 ${
                mint ? "text-mint/60" : "text-slate-600"
              }`}
            />
          )}
        </motion.li>
      ))}
    </ol>
  </motion.div>
);

export const Workflow = () => (
  <section
    id="difference"
    className="relative mx-auto max-w-6xl px-4 py-24 sm:px-6 sm:py-32 lg:px-8"
    data-testid="workflow-section"
  >
    <motion.p
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8 }}
      className="text-center font-mono text-xs uppercase tracking-[0.24em] text-mint"
    >
      02 — Why Truvon
    </motion.p>

    <motion.h2
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.9, ease: EASE }}
      data-testid="differentiation-statement"
      className="mx-auto mt-8 max-w-3xl text-center font-display text-2xl font-bold leading-snug tracking-tight text-slate-50 sm:text-3xl lg:text-4xl"
    >
      Every exception deserves more than an alert. Truvon{" "}
      <span className="text-mint">detects the problem</span>,{" "}
      <span className="text-mint">investigates what happened</span>,{" "}
      <span className="text-mint">takes the right action</span>, and drives it
      toward <span className="text-mint">resolution</span>.
    </motion.h2>

    <div className="mt-16 grid gap-6 md:grid-cols-2 md:gap-8">
      <Flow
        title="Traditional workflow"
        badge="Reactive · Manual"
        steps={traditional}
        mint={false}
        testid="workflow-traditional"
      />
      <Flow
        title="Truvon workflow"
        badge="Proactive · Automatic"
        steps={truvon}
        mint={true}
        testid="workflow-truvon"
      />
    </div>
  </section>
);
